import { api } from '../api.js';
import { createPaginationNavigationGuard } from '../pagination-navigation.js';
import { navigate } from '../router.js';
import { esc } from '../../../../src/shared/escape-html.js';
import { formatDateTime as fmtDate } from '../../../../src/shared/format-time.js';

const STAGE_LABELS = { 1: '1 - Heuristic', 2: '2 - OpenAI', 3: '3 - Claude' };

let _cursor = null;
let _nextCursor = null;
let _cursorHistory = [];
let _page = 1;
let _eventsGen = 0;
const _eventsNavigation = createPaginationNavigationGuard();
const PAGE_SIZE = 25;
let _listBound = false;

function resetEventPagination() {
  _eventsNavigation.reset();
  _cursor = null;
  _nextCursor = null;
  _cursorHistory = [];
  _page = 1;
}

async function loadEvents() {
  const gen = ++_eventsGen;
  const body = document.getElementById('mod-events-body');
  const status = document.getElementById('mod-events-status');
  if (!body) return;

  status.textContent = 'Loading...';
  body.innerHTML = '';

  const params = new URLSearchParams({ limit: PAGE_SIZE });
  if (_cursor) params.set('cursor', _cursor);
  const ch = document.getElementById('mod-filter-channel')?.value;
  const verdict = document.getElementById('mod-filter-verdict')?.value;
  const ipFilter = document.getElementById('mod-filter-ip')?.value?.trim();
  const unreviewed = document.getElementById('mod-filter-unreviewed')?.checked;
  if (ch) params.set('channel', ch);
  if (verdict) params.set('verdict', verdict);
  if (ipFilter) params.set('ip', ipFilter);
  if (unreviewed) params.set('unreviewed', 'true');

  try {
    const data = await api.get(`/api/admin/moderation/events?${params}`);
    if (gen !== _eventsGen) return 'stale';
    const { events } = data;
    _nextCursor = data.next_cursor;
    document.getElementById('mod-prev').disabled = _cursorHistory.length === 0;
    document.getElementById('mod-next').disabled = !data.has_more;
    document.getElementById('mod-page-info').textContent = `Page ${_page}`;

    if (events.length === 0) {
      status.textContent = _page > 1 ? 'No more events.' : 'No events found.';
      return true;
    }

    status.textContent = '';
    for (const e of events) {
      const tr = document.createElement('tr');
      const t = new Date(e.created_at).toLocaleString();
      const msg = (e.content_preview || '').slice(0, 60);
      const actionCell = e.reviewed_at
        ? esc(e.review_action)
        : `<select class="mod-inline-action" data-id="${e.id}"><option value="">--</option><option value="dismiss">Dismiss</option><option value="warn">Warn</option><option value="mute">Mute</option><option value="ban">Ban</option></select>`;
      const ipCell = e.ip ? `<span class="mod-ip-link" data-ip="${esc(e.ip)}">${esc(e.ip)}</span>` : '-';
      tr.innerHTML = `<td>${esc(t)}</td><td>${esc(e.channel)}</td><td>${esc(e.username || '?')}</td><td class="mod-user-id">${esc(e.user_id || '-')}</td><td>${ipCell}</td><td class="mod-msg-cell" title="${esc(e.content_preview)}">${esc(msg)}</td><td class="mod-verdict-${e.verdict}">${esc(e.verdict)}</td><td>${STAGE_LABELS[e.stage] || e.stage}</td><td>${actionCell}</td>`;
      tr.style.cursor = 'pointer';
      tr.addEventListener('click', (ev) => {
        if (ev.target.closest('.mod-inline-action')) return;
        navigate(`/admin/moderation/events/${e.id}`);
      });
      body.appendChild(tr);
    }

    body.querySelectorAll('.mod-ip-link').forEach(el => {
      el.style.cursor = 'pointer';
      el.style.textDecoration = 'underline';
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const ipInput = document.getElementById('mod-filter-ip');
        if (ipInput) { ipInput.value = el.dataset.ip; resetEventPagination(); loadEvents(); }
      });
    });

    body.querySelectorAll('.mod-inline-action').forEach(sel => {
      sel.addEventListener('change', async () => {
        const action = sel.value;
        if (!action) return;
        sel.disabled = true;
        try {
          await api.post(`/api/admin/moderation/events/${sel.dataset.id}/resolve`, { action });
          loadStats();
          loadEvents();
        } catch {
          sel.disabled = false;
          sel.value = '';
        }
      });
    });
    return true;

  } catch (err) {
    if (gen !== _eventsGen) return 'stale';
    status.textContent = `Error: ${err.message}`;
    return false;
  }
}

async function loadSanctions() {
  const body = document.getElementById('mod-sanctions-body');
  const status = document.getElementById('mod-sanctions-status');
  if (!body) return;

  status.textContent = 'Loading...';
  body.innerHTML = '';

  try {
    const { sanctions } = await api.get('/api/admin/moderation/sanctions');

    if (sanctions.length === 0) {
      status.textContent = 'No active sanctions.';
      return;
    }

    status.textContent = '';
    for (const s of sanctions) {
      const tr = document.createElement('tr');
      const mute = s.mute_until ? new Date(s.mute_until).toLocaleString() : '-';
      const ban = s.ban_until ? new Date(s.ban_until).toLocaleString() : '-';
      tr.innerHTML = `<td>${esc(s.user_id.slice(0, 12))}</td><td>${esc(mute)}</td><td>${esc(ban)}</td><td>${esc(s.reason || '')}</td><td><button class="mod-lift-btn" data-user="${esc(s.user_id)}">Lift</button></td>`;
      body.appendChild(tr);
    }

    body.querySelectorAll('.mod-lift-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        try {
          await api.delete(`/api/admin/moderation/sanctions/${btn.dataset.user}`);
          loadSanctions();
        } catch (err) {
          alert(`Failed: ${err.message}`);
        }
      });
    });
  } catch (err) {
    status.textContent = `Error: ${err.message}`;
  }
}

async function loadStats() {
  const bar = document.getElementById('mod-stats-bar');
  if (!bar) return;
  try {
    const { stats } = await api.get('/api/admin/moderation/stats');
    const s24 = stats['24h'] || [];
    const blocks24 = s24.filter(r => r.verdict === 'block').reduce((s, r) => s + r.count, 0);
    const warns24 = s24.filter(r => r.verdict === 'warn').reduce((s, r) => s + r.count, 0);
    bar.innerHTML = `
      <div class="mod-stat"><span class="mod-stat-num">${blocks24}</span><span class="mod-stat-label">Blocks (24h)</span></div>
      <div class="mod-stat"><span class="mod-stat-num">${warns24}</span><span class="mod-stat-label">Warns (24h)</span></div>
    `;
  } catch {}
}

async function loadIpSanctions() {
  const body = document.getElementById('mod-ip-sanctions-body');
  const status = document.getElementById('mod-ip-sanctions-status');
  if (!body) return;

  status.textContent = 'Loading...';
  body.innerHTML = '';

  try {
    const { sanctions } = await api.get('/api/admin/moderation/ip-sanctions');

    if (sanctions.length === 0) {
      status.textContent = 'No active IP sanctions.';
      return;
    }

    status.textContent = '';
    for (const s of sanctions) {
      const tr = document.createElement('tr');
      const mute = s.mute_until ? new Date(s.mute_until).toLocaleString() : '-';
      const ban = s.ban_until ? new Date(s.ban_until).toLocaleString() : '-';
      tr.innerHTML = `<td>${esc(s.ip)}</td><td>${esc(mute)}</td><td>${esc(ban)}</td><td>${esc(s.reason || '')}</td><td><button class="mod-lift-ip-btn" data-ip="${esc(s.ip)}">Lift</button></td>`;
      body.appendChild(tr);
    }

    body.querySelectorAll('.mod-lift-ip-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        try {
          await api.delete(`/api/admin/moderation/ip-sanctions/${encodeURIComponent(btn.dataset.ip)}`);
          loadIpSanctions();
        } catch (err) {
          alert(`Failed: ${err.message}`);
        }
      });
    });
  } catch (err) {
    status.textContent = `Error: ${err.message}`;
  }
}

let _ipBanBound = false;
function bindIpBanForm() {
  if (_ipBanBound) return;
  _ipBanBound = true;
  const btn = document.getElementById('mod-ip-ban-submit');
  if (!btn) return;
  btn.addEventListener('click', async () => {
    const ip = document.getElementById('mod-ip-ban-input')?.value?.trim();
    const action = document.getElementById('mod-ip-ban-action')?.value;
    const hours = document.getElementById('mod-ip-ban-hours')?.value;
    const reason = document.getElementById('mod-ip-ban-reason')?.value;
    if (!ip) return;
    btn.disabled = true;
    try {
      const body = { ip, action };
      if (hours?.trim()) body.duration_hours = hours;
      if (reason) body.reason = reason;
      await api.post('/api/admin/moderation/ip-sanctions', body);
      document.getElementById('mod-ip-ban-input').value = '';
      document.getElementById('mod-ip-ban-hours').value = '';
      document.getElementById('mod-ip-ban-reason').value = '';
      loadIpSanctions();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    }
    btn.disabled = false;
  });
}

function bindFilters() {
  if (_listBound) return;
  _listBound = true;

  const reload = () => { resetEventPagination(); loadEvents(); };
  document.getElementById('mod-filter-channel')?.addEventListener('change', reload);
  document.getElementById('mod-filter-verdict')?.addEventListener('change', reload);
  document.getElementById('mod-filter-unreviewed')?.addEventListener('change', reload);

  let _ipDebounce;
  document.getElementById('mod-filter-ip')?.addEventListener('input', () => {
    clearTimeout(_ipDebounce);
    _ipDebounce = setTimeout(reload, 400);
  });

  document.getElementById('mod-prev')?.addEventListener('click', async () => {
    if (!_cursorHistory.length) return;
    await _eventsNavigation.run(async () => {
      const snap = { cursor: _cursor, page: _page, history: [..._cursorHistory] };
      _cursor = _cursorHistory.pop() || null;
      _page--;
      if (!await loadEvents()) {
        _cursor = snap.cursor; _page = snap.page; _cursorHistory = snap.history;
      }
    });
  });
  document.getElementById('mod-next')?.addEventListener('click', async () => {
    if (!_nextCursor) return;
    await _eventsNavigation.run(async () => {
      const snap = { cursor: _cursor, page: _page, history: [..._cursorHistory] };
      _cursorHistory.push(_cursor);
      _cursor = _nextCursor;
      _page++;
      if (!await loadEvents()) {
        _cursor = snap.cursor; _page = snap.page; _cursorHistory = snap.history;
      }
    });
  });
}

// ── Pipeline Test ────────────────────────────────────────

async function loadPipelineStatus() {
  const bar = document.getElementById('mod-test-status-bar');
  if (!bar) return;
  try {
    const s = await api.get('/api/admin/moderation/status');
    const dot = (active) => `<span class="mod-test-status-dot ${active ? 'active' : 'inactive'}"></span>`;
    bar.innerHTML = [
      `${dot(s.stage1_heuristics)} Heuristics`,
      `${dot(s.stage2_openai)} OpenAI`,
      `${dot(s.stage3_claude)} Claude`,
      `${dot(s.avatar_openai)} Avatar`,
      `${dot(s.voice_moderation)} Voice`,
    ].join('');
  } catch {}
}

function formatTestResult(data) {
  const lines = [];
  for (const [key, val] of Object.entries(data)) {
    const LABELS = { stage1: 'Stage 1: Heuristics', stage2: 'Stage 2: OpenAI', stage3: 'Stage 3: Claude', stage1_openai: 'Stage 1: OpenAI Image', stage2_claude: 'Stage 2: Claude Vision', avatar: 'Avatar' };
    const label = LABELS[key] || key.replace('stage', 'Stage ');
    if (val.pass !== undefined) {
      const color = val.pass ? '#4ade80' : '#f87171';
      lines.push(`<span style="color:${color};font-weight:bold">${esc(label)}: ${val.pass ? 'PASS' : 'BLOCKED'}</span>`);
      if (val.reason) lines.push(`  Reason: ${esc(val.reason)}`);
    } else if (val.verdict) {
      const colors = { allow: '#4ade80', warn: '#fbbf24', block: '#f87171', confident_block: '#f87171', skip: '#888', error: '#f87171', borderline: '#fbbf24' };
      lines.push(`<span style="color:${colors[val.verdict] || '#ccc'};font-weight:bold">${esc(label)}: ${esc(val.verdict.toUpperCase())}</span>`);
      if (val.severity) lines.push(`  Severity: ${val.severity}/3`);
      if (val.categories && Array.isArray(val.categories) && val.categories.length) {
        lines.push(`  Categories: ${val.categories.map(c => esc(String(c))).join(', ')}`);
      }
      if (val.reasoning) lines.push(`  Reasoning: ${esc(val.reasoning)}`);
      if (val.reason) lines.push(`  Reason: ${esc(val.reason)}`);
      if (val.scores && typeof val.scores === 'object') {
        const top = Object.entries(val.scores)
          .filter(([, v]) => typeof v === 'number' && v > 0.01)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([k, v]) => `${k}: ${(v * 100).toFixed(1)}%`);
        if (top.length) lines.push(`  Scores: ${top.join(', ')}`);
      }
    }
  }
  return lines.join('\n');
}

let _testBound = false;
function bindTestPanel() {
  if (_testBound) return;
  _testBound = true;

  async function runTest(inputId, resultId, btnId, stage) {
    const input = document.getElementById(inputId);
    const result = document.getElementById(resultId);
    const btn = document.getElementById(btnId);
    const text = input?.value?.trim();
    if (!text) return;
    btn.disabled = true;
    result.hidden = false;
    result.textContent = 'Running...';
    try {
      const body = { text };
      if (stage) body.stage = stage;
      const data = await api.post('/api/admin/moderation/test', body);
      result.innerHTML = formatTestResult(data)
        + '\n\n<span style="color:var(--text-dim)">Raw JSON:</span>\n'
        + esc(JSON.stringify(data, null, 2));
    } catch (err) {
      result.textContent = `Error: ${err.message}`;
    }
    btn.disabled = false;
  }

  document.getElementById('mod-test-s1-btn')?.addEventListener('click', () =>
    runTest('mod-test-s1', 'mod-test-s1-result', 'mod-test-s1-btn', 1));
  document.getElementById('mod-test-s2-btn')?.addEventListener('click', () =>
    runTest('mod-test-s2', 'mod-test-s2-result', 'mod-test-s2-btn', 2));
  document.getElementById('mod-test-s3-btn')?.addEventListener('click', () =>
    runTest('mod-test-s3', 'mod-test-s3-result', 'mod-test-s3-btn', 3));
  document.getElementById('mod-test-full-btn')?.addEventListener('click', () =>
    runTest('mod-test-full', 'mod-test-full-result', 'mod-test-full-btn', null));

  const avatarFileInput = document.getElementById('mod-test-avatar-file');
  const avatarPreview = document.getElementById('mod-test-avatar-preview');

  avatarFileInput?.addEventListener('change', () => {
    const file = avatarFileInput.files?.[0];
    if (file && avatarPreview) {
      const url = URL.createObjectURL(file);
      avatarPreview.innerHTML = `<img src="${url}" style="max-width:128px;max-height:128px;border-radius:8px;border:1px solid var(--border)">`;
      avatarPreview.hidden = false;
    }
  });

  async function runAvatarTest(resultId, btnId, stage) {
    const file = avatarFileInput?.files?.[0];
    const result = document.getElementById(resultId);
    const btn = document.getElementById(btnId);
    if (!file) { result.hidden = false; result.textContent = 'Select an image first.'; return; }

    btn.disabled = true;
    result.hidden = false;
    result.textContent = 'Running...';
    try {
      const buf = await file.arrayBuffer();
      const url = stage
        ? `/api/admin/moderation/test-avatar?stage=${stage}`
        : '/api/admin/moderation/test-avatar';
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': file.type },
        body: buf,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      result.innerHTML = formatTestResult(data)
        + '\n\n<span style="color:var(--text-dim)">Raw JSON:</span>\n'
        + esc(JSON.stringify(data, null, 2));
    } catch (err) {
      result.textContent = `Error: ${err.message}`;
    }
    btn.disabled = false;
  }

  document.getElementById('mod-test-avatar-s1-btn')?.addEventListener('click', () =>
    runAvatarTest('mod-test-avatar-s1-result', 'mod-test-avatar-s1-btn', 1));
  document.getElementById('mod-test-avatar-s2-btn')?.addEventListener('click', () =>
    runAvatarTest('mod-test-avatar-s2-result', 'mod-test-avatar-s2-btn', 2));
  document.getElementById('mod-test-avatar-full-btn')?.addEventListener('click', () =>
    runAvatarTest('mod-test-avatar-full-result', 'mod-test-avatar-full-btn', null));
}

// ── Detail ───────────────────────────────────────────────

function metaRow(label, value) {
  return `<span class="mod-detail-label">${label}</span><span class="mod-detail-value">${value}</span>`;
}

async function loadModerationDetail(eventId) {
  const el = document.getElementById('mod-detail-content');
  if (!el) return;
  el.innerHTML = '<div class="admin-status">Loading...</div>';

  try {
    const e = await api.get(`/api/admin/moderation/events/${eventId}`);

    const cats = e.categories || {};
    const ce = typeof cats === 'object' && !Array.isArray(cats)
      ? Object.entries(cats).filter(([, v]) => v > 0.1).map(([k, v]) => `${k}: ${(v*100)|0}%`)
      : (Array.isArray(cats) ? cats : []);
    const tags = ce.length ? `<div class="mod-category-tags">${ce.map(c => `<span class="mod-category-tag">${esc(c)}</span>`).join('')}</div>` : '';

    const reasoning = `<div class="mod-detail-reasoning"><div class="mod-detail-reasoning-label">AI Reasoning</div>${e.reasoning ? esc(e.reasoning) : '<em style="color:var(--text-dim)">Not available</em>'}</div>`;
    const sev = e.severity != null ? ` <span class="mod-severity-badge mod-severity-${e.severity}">Severity ${e.severity}</span>` : '';

    const actionHtml = e.reviewed_at
      ? `<div class="mod-detail-resolved">Resolved as <strong>${esc(e.review_action)}</strong> on ${fmtDate(e.reviewed_at)}</div>`
      : `<div class="card admin-card"><div class="card-label">TAKE ACTION</div><div class="mod-detail-actions">
          <div class="mod-detail-actions-row"><select id="mod-action-select"><option value="">Select action...</option><option value="dismiss">Dismiss</option><option value="warn">Warn</option><option value="mute">Mute</option><option value="ban">Ban</option></select>
          <input type="number" id="mod-action-hours" placeholder="Hours (max 8760)" min="1" max="8760" style="width:140px;display:none"></div>
          <textarea id="mod-action-reason" placeholder="Reason (optional)" rows="2"></textarea>
          <div class="mod-detail-actions-row"><button id="mod-action-submit" class="btn btn-mode btn-primary" disabled>Submit</button><span id="mod-action-status"></span></div></div></div>`;

    const isImage = e.content_type === 'image' || e.channel === 'avatar';
    const contentCard = isImage
      ? `<div class="card admin-card"><div class="card-label">FLAGGED AVATAR</div>
          <div style="display:flex;align-items:center;gap:16px;padding:8px 0">${e.username ? `<img src="/api/users/${encodeURIComponent(e.username)}/avatar?v=${Date.now()}" style="width:64px;height:64px;border-radius:8px;object-fit:cover" onerror="this.style.display='none'">` : ''}<span style="color:var(--text-dim)">${e.user_id ? esc(e.username || e.user_id) : 'unknown user'}</span></div>${tags}`
      : `<div class="card admin-card"><div class="card-label">FLAGGED MESSAGE</div>
          <div class="mod-detail-message">${esc(e.content_text)}</div>${tags}`;

    el.innerHTML = `${contentCard}
      <div class="mod-detail-meta">${metaRow('Time', fmtDate(e.created_at))}${metaRow('User', (e.username ? esc(e.username) + ' ' : '') + '<span class="mod-user-id">' + esc(e.user_id || 'anonymous') + '</span>')}${e.ip ? metaRow('IP', esc(e.ip)) : ''}${metaRow('Channel', esc(e.channel))}${e.room_code ? metaRow('Room', esc(e.room_code)) : ''}${metaRow('Stage', STAGE_LABELS[e.stage] || e.stage)}${metaRow('Verdict', `<span class="mod-verdict-${e.verdict}">${esc(e.verdict)}</span>${sev}`)}</div></div>${reasoning}${actionHtml}`;

    if (!e.reviewed_at) bindActionForm(eventId);
  } catch (err) {
    el.innerHTML = `<div class="admin-status" style="color:#f87171">Failed: ${esc(err.message)}</div>`;
  }
}

function bindActionForm(eventId) {
  const select = document.getElementById('mod-action-select');
  const hours = document.getElementById('mod-action-hours');
  const btn = document.getElementById('mod-action-submit');
  const status = document.getElementById('mod-action-status');
  select.addEventListener('change', () => {
    btn.disabled = !select.value;
    hours.style.display = (select.value === 'mute' || select.value === 'ban') ? '' : 'none';
  });
  btn.addEventListener('click', async () => {
    btn.disabled = true;
    status.textContent = 'Saving...';
    try {
      const body = { action: select.value, reason: document.getElementById('mod-action-reason').value };
      if (hours.value.trim()) body.duration_hours = hours.value;
      await api.post(`/api/admin/moderation/events/${eventId}/resolve`, body);
      status.textContent = 'Done.';
      status.style.color = '#4ade80';
      setTimeout(() => navigate('/admin/moderation'), 600);
    } catch (err) {
      status.textContent = `Error: ${err.message}`;
      status.style.color = '#f87171';
      btn.disabled = false;
    }
  });
}

// ── Avatar Events ───────────────────────────────────────

let _avatarCursor = null;
let _avatarNextCursor = null;
let _avatarCursorHistory = [];
let _avatarPage = 1;
let _avatarGen = 0;
const _avatarNavigation = createPaginationNavigationGuard();
const AVATAR_PAGE_SIZE = 25;

function resetAvatarPagination() {
  _avatarNavigation.reset();
  _avatarCursor = null;
  _avatarNextCursor = null;
  _avatarCursorHistory = [];
  _avatarPage = 1;
}

async function loadAvatarEvents() {
  const gen = ++_avatarGen;
  const body = document.getElementById('mod-avatar-body');
  const status = document.getElementById('mod-avatar-status');
  if (!body) return;

  status.textContent = 'Loading...';
  body.innerHTML = '';

  const params = new URLSearchParams({
    limit: AVATAR_PAGE_SIZE,
    channel: 'avatar',
  });
  if (_avatarCursor) params.set('cursor', _avatarCursor);

  try {
    const data = await api.get(`/api/admin/moderation/events?${params}`);
    if (gen !== _avatarGen) return 'stale';
    const { events } = data;
    _avatarNextCursor = data.next_cursor;
    document.getElementById('mod-avatar-prev').disabled = _avatarCursorHistory.length === 0;
    document.getElementById('mod-avatar-next').disabled = !data.has_more;
    document.getElementById('mod-avatar-page-info').textContent = `Page ${_avatarPage}`;

    if (events.length === 0) {
      status.textContent = _avatarPage > 1 ? 'No more events.' : 'No avatar checks yet.';
      return true;
    }

    status.textContent = '';
    for (const e of events) {
      const tr = document.createElement('tr');
      const t = new Date(e.created_at).toLocaleString();

      const avatarCell = e.username
        ? `<img src="/api/users/${encodeURIComponent(e.username)}/avatar?v=${Date.now()}" style="width:32px;height:32px;border-radius:4px;object-fit:cover" onerror="this.replaceWith(document.createTextNode('–'))">`
        : '–';

      const preview = formatAvatarModerationSummary(e);

      const actionCell = e.reviewed_at
        ? esc(e.review_action)
        : `<select class="mod-inline-action" data-id="${e.id}"><option value="">--</option><option value="dismiss">Dismiss</option><option value="warn">Warn</option><option value="mute">Mute</option><option value="ban">Ban</option></select>`;

      tr.innerHTML = `<td>${esc(t)}</td><td>${esc(e.username || '?')}</td><td>${avatarCell}</td><td class="mod-verdict-${e.verdict}">${esc(e.verdict)}</td><td>${STAGE_LABELS[e.stage] || e.stage}</td><td title="${esc(preview)}" style="max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(preview) || '–'}</td><td>${actionCell}</td>`;
      tr.style.cursor = 'pointer';
      tr.addEventListener('click', (ev) => {
        if (ev.target.closest('.mod-inline-action')) return;
        navigate(`/admin/moderation/events/${e.id}`);
      });
      body.appendChild(tr);
    }

    body.querySelectorAll('.mod-inline-action').forEach(sel => {
      sel.addEventListener('change', async () => {
        const action = sel.value;
        if (!action) return;
        sel.disabled = true;
        try {
          await api.post(`/api/admin/moderation/events/${sel.dataset.id}/resolve`, { action });
          loadStats();
          loadAvatarEvents();
        } catch {
          sel.disabled = false;
          sel.value = '';
        }
      });
    });
    return true;

  } catch (err) {
    if (gen !== _avatarGen) return 'stale';
    status.textContent = `Error: ${err.message}`;
    return false;
  }
}

export function formatAvatarModerationSummary({ categories, reasoning } = {}) {
  let categoryText = '';
  if (categories && typeof categories === 'object' && !Array.isArray(categories)) {
    categoryText = Object.entries(categories)
      .filter(([, value]) => typeof value === 'number' && value > 0.01)
      .sort(([, left], [, right]) => right - left)
      .slice(0, 3)
      .map(([name, value]) => `${name}: ${(value * 100).toFixed(0)}%`)
      .join(', ');
  } else if (Array.isArray(categories)) {
    categoryText = categories.join(', ');
  }
  return reasoning
    ? (categoryText ? `${categoryText}. ${reasoning}` : reasoning)
    : categoryText;
}

function bindAvatarPagination() {
  document.getElementById('mod-avatar-prev')?.addEventListener('click', async () => {
    if (!_avatarCursorHistory.length) return;
    await _avatarNavigation.run(async () => {
      const snap = { cursor: _avatarCursor, page: _avatarPage, history: [..._avatarCursorHistory] };
      _avatarCursor = _avatarCursorHistory.pop() || null;
      _avatarPage--;
      if (!await loadAvatarEvents()) {
        _avatarCursor = snap.cursor; _avatarPage = snap.page; _avatarCursorHistory = snap.history;
      }
    });
  });
  document.getElementById('mod-avatar-next')?.addEventListener('click', async () => {
    if (!_avatarNextCursor) return;
    await _avatarNavigation.run(async () => {
      const snap = { cursor: _avatarCursor, page: _avatarPage, history: [..._avatarCursorHistory] };
      _avatarCursorHistory.push(_avatarCursor);
      _avatarCursor = _avatarNextCursor;
      _avatarPage++;
      if (!await loadAvatarEvents()) {
        _avatarCursor = snap.cursor; _avatarPage = snap.page; _avatarCursorHistory = snap.history;
      }
    });
  });
}

// ── Exports ──────────────────────────────────────────────

export function renderList() {
  resetEventPagination();
  resetAvatarPagination();
  bindFilters();
  bindIpBanForm();
  bindAvatarPagination();
  bindTestPanel();
  loadStats();
  loadEvents();
  loadAvatarEvents();
  loadSanctions();
  loadIpSanctions();
  loadPipelineStatus();
}

export function renderDetail(params) {
  loadModerationDetail(params.id);
}
